import React, { useState, useEffect } from 'react';
import { apiClient } from '../../utils/api';
import { toast } from 'sonner';
import {
  Loader2, Plus, Edit2, Trash2, Eye, EyeOff, Star, FileText, Search, Calendar
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import ImageUploader from '../../components/admin/ImageUploader';

const emptyForm = {
  title: '',
  slug: '',
  excerpt: '',
  content: '',
  featured_image: '',
  tags: '',
  seo_title: '',
  seo_description: '',
  status: 'draft',
  featured: false,
  published: false
};

const slugify = (s) =>
  s.toString().toLowerCase().trim()
    .replace(/[\u0400-\u04FF]/g, (c) => {
      const map = {
        'а':'a','б':'b','в':'v','г':'g','д':'d','е':'e','ж':'zh','з':'z','и':'i','й':'y',
        'к':'k','л':'l','м':'m','н':'n','о':'o','п':'p','р':'r','с':'s','т':'t','у':'u',
        'ф':'f','х':'h','ц':'ts','ч':'ch','ш':'sh','щ':'sht','ъ':'a','ь':'','ю':'yu','я':'ya'
      };
      return map[c.toLowerCase()] || '';
    })
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-');

const NewsManager = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [search, setSearch] = useState('');
  const [formData, setFormData] = useState(emptyForm);

  useEffect(() => { fetchPosts(); }, []);

  const fetchPosts = async () => {
    try {
      setLoading(true);
      const data = await apiClient.get('/api/blog/?limit=200');
      setPosts(data);
    } catch (e) {
      toast.error('Грешка при зареждане');
    } finally {
      setLoading(false);
    }
  };

  const openCreate = () => {
    setEditing(null);
    setFormData(emptyForm);
    setIsModalOpen(true);
  };

  const openEdit = (p) => {
    setEditing(p);
    setFormData({
      ...emptyForm,
      ...p,
      tags: (p.tags || []).join(', '),
      featured_image: p.featured_image || ''
    });
    setIsModalOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = {
        title: formData.title,
        slug: formData.slug || slugify(formData.title),
        excerpt: formData.excerpt || null,
        content: formData.content,
        featured_image: formData.featured_image || null,
        tags: formData.tags.split(',').map(s => s.trim()).filter(Boolean),
        seo_title: formData.seo_title || null,
        seo_description: formData.seo_description || null,
        seo_keywords: [],
        status: formData.published ? 'published' : 'draft',
        published: !!formData.published,
        featured: !!formData.featured
      };

      if (editing) {
        await apiClient.put(`/api/blog/${editing._id}`, payload);
        toast.success('Статията е обновена');
      } else {
        await apiClient.post('/api/blog/', payload);
        toast.success('Статията е създадена');
      }
      setIsModalOpen(false);
      fetchPosts();
    } catch (err) {
      toast.error(err.message || 'Грешка');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Сигурни ли сте?')) return;
    try {
      await apiClient.delete(`/api/blog/${id}`);
      toast.success('Изтрита');
      fetchPosts();
    } catch { toast.error('Грешка'); }
  };

  const togglePublish = async (p) => {
    try {
      await apiClient.patch(`/api/blog/${p._id}/publish?published=${!p.published}`);
      toast.success(p.published ? 'Скрита' : 'Публикувана');
      fetchPosts();
    } catch { toast.error('Грешка'); }
  };

  const toggleFeatured = async (p) => {
    try {
      await apiClient.patch(`/api/blog/${p._id}/featured?featured=${!p.featured}`);
      fetchPosts();
    } catch { toast.error('Грешка'); }
  };

  const filtered = posts.filter(p =>
    !search || p.title?.toLowerCase().includes(search.toLowerCase())
  );

  if (loading && posts.length === 0) {
    return <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 text-[#D4AF37] animate-spin" /></div>;
  }

  return (
    <div className="space-y-6" data-testid="news-manager">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-3xl font-serif font-semibold text-[#F6F2E8]">Анализи & Новини</h1>
          <p className="text-[#A9A9A9] mt-1">{posts.length} статии общо</p>
        </div>
        <Button onClick={openCreate} className="btn-primary" data-testid="add-post-btn">
          <Plus className="w-4 h-4 mr-2" /> Нова статия
        </Button>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#A9A9A9]" />
        <Input
          placeholder="Търсене..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-10 bg-[#0B0B0B] border-[#D4AF37]/20"
          data-testid="news-search"
        />
      </div>

      {/* List */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filtered.map((p) => (
          <div key={p._id} className="bg-[#111111] border border-[#D4AF37]/10 rounded-xl overflow-hidden hover:border-[#D4AF37]/30 transition-all" data-testid={`post-card-${p._id}`}>
            <div className="h-44 bg-gradient-to-br from-[#D4AF37]/20 to-[#B8892E]/20 flex items-center justify-center relative overflow-hidden">
              {p.featured_image ? (
                <>
                  <img
                    src={p.featured_image.startsWith('http') ? p.featured_image : `${process.env.REACT_APP_BACKEND_URL}${p.featured_image}`}
                    alt={p.title}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.currentTarget.onerror = null;
                      e.currentTarget.style.display = 'none';
                    }}
                  />
                </>
              ) : (
                <FileText className="w-14 h-14 text-[#D4AF37]/40" />
              )}
              {!p.published && (
                <span className="absolute top-2 left-2 px-2 py-1 bg-yellow-500/90 text-black text-xs font-medium rounded">Чернова</span>
              )}
              {p.featured && (
                <Star className="absolute top-2 right-2 w-5 h-5 text-[#D4AF37] fill-[#D4AF37]" />
              )}
            </div>

            <div className="p-4 space-y-3">
              <h3 className="font-serif font-semibold text-[#F6F2E8] line-clamp-2">{p.title}</h3>
              {p.excerpt && <p className="text-sm text-[#A9A9A9] line-clamp-2">{p.excerpt}</p>}

              <div className="flex items-center gap-3 text-xs text-[#A9A9A9]">
                <span className="flex items-center gap-1">
                  <Calendar size={12} />
                  {new Date(p.published_at || p.created_at).toLocaleDateString('bg-BG')}
                </span>
                <span>{p.views_count || 0} прегл.</span>
              </div>

              <div className="flex items-center gap-2 pt-3 border-t border-[#D4AF37]/10">
                <button onClick={() => togglePublish(p)} className="flex-1 flex items-center justify-center gap-1 px-2 py-2 text-xs bg-[#0B0B0B] rounded hover:bg-[#D4AF37]/10" data-testid={`publish-post-${p._id}`}>
                  {p.published ? <EyeOff size={14} /> : <Eye size={14} />}
                  <span>{p.published ? 'Скрий' : 'Публ.'}</span>
                </button>
                <button onClick={() => toggleFeatured(p)} className="p-2 bg-[#0B0B0B] rounded hover:bg-[#D4AF37]/10">
                  <Star size={14} className={p.featured ? 'fill-[#D4AF37] text-[#D4AF37]' : 'text-[#A9A9A9]'} />
                </button>
                <button onClick={() => openEdit(p)} className="p-2 bg-[#0B0B0B] rounded hover:bg-[#D4AF37]/10" data-testid={`edit-post-${p._id}`}>
                  <Edit2 size={14} className="text-[#D4AF37]" />
                </button>
                <button onClick={() => handleDelete(p._id)} className="p-2 bg-[#0B0B0B] rounded hover:bg-red-500/10" data-testid={`delete-post-${p._id}`}>
                  <Trash2 size={14} className="text-red-400" />
                </button>
              </div>
            </div>
          </div>
        ))}

        {filtered.length === 0 && (
          <div className="col-span-full text-center py-12 text-[#A9A9A9]">
            Все още няма статии. Създайте първата.
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="bg-[#111111] border-[#D4AF37]/20 text-[#F6F2E8] max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="post-modal">
          <DialogHeader>
            <DialogTitle className="text-2xl font-serif">
              {editing ? 'Редактирай статия' : 'Нова статия'}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-5 mt-4">
            <div>
              <Label className="block mb-2">Главно изображение</Label>
              <ImageUploader
                value={formData.featured_image}
                onChange={(url) => setFormData({...formData, featured_image: url})}
              />
            </div>

            <div>
              <Label>Заглавие *</Label>
              <Input
                required
                value={formData.title}
                onChange={(e) => {
                  const title = e.target.value;
                  setFormData({...formData, title, slug: editing ? formData.slug : slugify(title)});
                }}
                className="bg-[#0B0B0B] border-[#D4AF37]/20"
                data-testid="post-title-input"
              />
            </div>

            <div>
              <Label>Slug</Label>
              <Input value={formData.slug} onChange={(e) => setFormData({...formData, slug: e.target.value})} className="bg-[#0B0B0B] border-[#D4AF37]/20" />
            </div>

            <div>
              <Label>Кратко резюме</Label>
              <Textarea rows={2} value={formData.excerpt} onChange={(e) => setFormData({...formData, excerpt: e.target.value})} className="bg-[#0B0B0B] border-[#D4AF37]/20 resize-none" />
            </div>

            <div>
              <Label>Съдържание *</Label>
              <Textarea required rows={10} value={formData.content} onChange={(e) => setFormData({...formData, content: e.target.value})} className="bg-[#0B0B0B] border-[#D4AF37]/20 resize-none font-mono text-sm" placeholder="Можете да използвате HTML тагове..." data-testid="post-content-input" />
              <p className="text-xs text-[#A9A9A9] mt-1">Поддържа HTML.</p>
            </div>

            <div>
              <Label>Тагове (разделени със запетая)</Label>
              <Input value={formData.tags} onChange={(e) => setFormData({...formData, tags: e.target.value})} className="bg-[#0B0B0B] border-[#D4AF37]/20" placeholder="инвестиции, пазар, анализ" />
            </div>

            <div className="pt-2 border-t border-[#D4AF37]/10">
              <p className="text-sm font-medium mb-2 text-[#D4AF37]">SEO</p>
            </div>

            <div>
              <Label>SEO заглавие</Label>
              <Input value={formData.seo_title} onChange={(e) => setFormData({...formData, seo_title: e.target.value})} className="bg-[#0B0B0B] border-[#D4AF37]/20" />
            </div>

            <div>
              <Label>SEO описание</Label>
              <Textarea rows={2} value={formData.seo_description} onChange={(e) => setFormData({...formData, seo_description: e.target.value})} className="bg-[#0B0B0B] border-[#D4AF37]/20 resize-none" />
            </div>

            <div className="flex items-center gap-6 pt-2">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={formData.published} onChange={(e) => setFormData({...formData, published: e.target.checked})} className="w-4 h-4" data-testid="post-published-checkbox" />
                <span className="text-sm">Публикувана</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={formData.featured} onChange={(e) => setFormData({...formData, featured: e.target.checked})} className="w-4 h-4" />
                <span className="text-sm">Препоръчана (Featured)</span>
              </label>
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t border-[#D4AF37]/10">
              <Button type="button" onClick={() => setIsModalOpen(false)} className="btn-secondary">Отказ</Button>
              <Button type="submit" disabled={submitting} className="btn-primary" data-testid="post-submit-btn">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : (editing ? 'Обнови' : 'Създай')}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default NewsManager;
